import isEmpty from 'lodash/isEmpty';
import { useAppendDraftComment } from 'pages/WellGuardian/WIR/hooks';
import { getKomTechnicalWirReactiveScopeData } from 'pages/WellGuardian/WIR/utils';
import { WIRKofMeetingTechnicalWirSections } from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useSelectUser } from 'redux/User/selectors';
import { useCountryState, useSelectedWIR } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WIR } from 'types/wellGuardian';

export const useSectionBudgetData = () => {
  const wirFromState = useSelectedWIR();
  const user = useSelectUser();
  const userName = useMemo(() => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(), [user]);
  const { appendDraftComment } = useAppendDraftComment();
  const countryState = useCountryState();

  const budgetPayload = useMemo(() => {
    const { phase, budget } = getKomTechnicalWirReactiveScopeData(wirFromState, countryState);
    const completedDate = budget?.completedDate;
    const assignedTo = budget?.assignedTo;
    const comments = appendDraftComment(budget?.comments || []);
    const attachments = budget?.attachments;
    const initialBudget = budget?.initialBudget;
    const expectedInterventionDuration = budget?.expectedInterventionDuration;
    const sectionCompleteConfirmation = budget?.sectionCompleteConfirmation;
    const budgetClassification = budget?.budgetClassification;
    const budgetClassJustification = budget?.budgetClassJustification;

    const isTicked =
      !isEmpty(expectedInterventionDuration?.toString()) &&
      !isEmpty(initialBudget) &&
      !isEmpty(budgetClassification);

    return {
      isTicked,
      assignedTo,
      comments,
      attachments,
      initialBudget,
      expectedInterventionDuration,
      sectionCompleteConfirmation,
      budgetClassification,
      budgetClassJustification,
      lastUpdatedDate: new Date().toISOString(),
      completedDate: isTicked ? new Date().toISOString() : completedDate,
      completedBy: isTicked ? userName : budget?.completedBy,
      phase,
    };
  }, [wirFromState, userName, appendDraftComment, countryState]);

  const saveBudget = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      if (wirFromState?.wirNumber && section === WIRKofMeetingTechnicalWirSections.budget) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRBudget(
            wirFromState?.wirNumber,
            budgetPayload,
            countryState,
          ),
        );
      }
    },
    [wirFromState, budgetPayload, countryState],
  );

  return {
    saveBudget,
    budgetPayload,
  };
};
